import { ChangeEvent, FunctionComponent, useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Stack,
    TextField,
    Typography,
    TypographyProps,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AgencyWithCredentials } from '@/models/agency';
import { useGetAllLiftOperatorsQuery } from '@/api/v2/liftOperator';
import { LiftOperator } from '@/models/liftoperator';
import { usePostAgencyMutation } from '@/api/v2/agency';
import { useNotification } from '@starjack/features/notifications';
import isEqual from 'lodash/isEqual';
import { useDirtyGuard } from '@starjack/shared-components';

const typographySyling: TypographyProps['sx'] = {
    width: '100%',
};

type AgencyDrawerContentProps = {
    agency: AgencyWithCredentials;
    onClose: () => void;
};

export const AgencyDrawerContent: FunctionComponent<
    AgencyDrawerContentProps
> = ({ agency, onClose }) => {
    const { data: liftOperators } = useGetAllLiftOperatorsQuery();
    const [postAgency, { isLoading }] = usePostAgencyMutation();
    const [localAgency, setLocalAgency] =
        useState<AgencyWithCredentials>(agency);
    useDirtyGuard(!isEqual(localAgency, agency));
    const { addNotification } = useNotification();

    const onInputChange =
        (key: keyof AgencyWithCredentials) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            setLocalAgency((prevState) => ({
                ...prevState,
                [key]: value,
            }));
        };

    const onSaveAgency = () => {
        postAgency(localAgency)
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Agentur erfolgreich angelegt',
                    type: 'success',
                });
                onClose();
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Anlegen. Bitte später noch einmal versuchen',
                });
            });
    };

    useEffect(() => {
        setLocalAgency(agency);
    }, [agency]);

    if (!localAgency) return null;

    return (
        <Box sx={{ width: '440px' }}>
            <Box>
                <Typography
                    variant='h6'
                    sx={{
                        bgcolor: 'primary.dark',
                        color: '#fff',
                        p: 4,
                    }}
                >
                    Neue Agentur anlegen
                </Typography>
            </Box>

            <Stack
                sx={{
                    p: 4,
                }}
                spacing={3}
            >
                <Autocomplete
                    options={getSortedLiftOperators(liftOperators)}
                    getOptionLabel={(option: LiftOperator) => option.name.de}
                    getOptionKey={(option: LiftOperator) => option.id}
                    slotProps={{
                        paper: {
                            sx: {
                                mt: 1,
                                boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.32)',
                            },
                        },
                    }}
                    value={
                        liftOperators?.find(
                            (op) => op.id === localAgency.liftOperatorId
                        ) || null
                    }
                    onChange={(_, value) => {
                        setLocalAgency((prevWebshop) => ({
                            ...prevWebshop,
                            liftOperatorId: value?.id || '',
                        }));
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label='Bergbahn auswählen'
                            sx={{ ...typographySyling }}
                        />
                    )}
                />

                <TextField
                    sx={{ ...typographySyling }}
                    onChange={onInputChange('username')}
                    value={localAgency.username}
                    label='Name'
                    required
                />

                <TextField
                    sx={{ ...typographySyling }}
                    type={'password'}
                    onChange={onInputChange('password')}
                    value={localAgency.password}
                    label='Passwort'
                    required
                />

                <LoadingButton
                    sx={{ ...typographySyling }}
                    variant='contained'
                    onClick={onSaveAgency}
                    disabled={
                        !localAgency.username ||
                        !localAgency.password ||
                        !localAgency.liftOperatorId
                    }
                    loading={isLoading}
                >
                    Speichern
                </LoadingButton>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <Button
                        sx={{
                            width: 200,
                            mx: 'auto',
                        }}
                        onClick={onClose}
                        variant='outlined'
                    >
                        Abbrechen
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
};

const getSortedLiftOperators = (liftOperators?: LiftOperator[]) => {
    return (
        liftOperators
            ?.slice()
            .sort((a, b) => a.name.de.localeCompare(b.name.de)) || []
    );
};
